import { PLATFORM_DEFAULT_APP_DISPLAY_NAME } from "@/config/productDisplay";
import type { OrgSettings } from "./orgSettingsSectionShared";

export function OrgSettingsSectionBrandingGrid(props: {
  settings: OrgSettings;
  loading: boolean;
  syncing: boolean;
  setSettings: React.Dispatch<React.SetStateAction<OrgSettings>>;
  onSyncFromGhl: () => void;
}) {
  const { settings, loading, syncing, setSettings, onSyncFromGhl } = props;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="space-y-1">
        <label className="text-xs font-medium text-gray-600">Brand name</label>
        <input
          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
          placeholder="e.g. Acme Shed Co."
          value={settings.brand_name}
          onChange={(e) => setSettings((s) => ({ ...s, brand_name: e.target.value }))}
          disabled={loading}
        />
        <p className="text-xs text-gray-400">Replaces the hardcoded company name in the app header.</p>
      </div>

      <div className="space-y-1">
        <label className="text-xs font-medium text-gray-600">App display name</label>
        <input
          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
          placeholder={`e.g. ${PLATFORM_DEFAULT_APP_DISPLAY_NAME}`}
          value={settings.app_display_name}
          onChange={(e) => setSettings((s) => ({ ...s, app_display_name: e.target.value }))}
          disabled={loading}
        />
        <p className="text-xs text-gray-400">The name shown for the AI assistant.</p>
      </div>

      <div className="space-y-1">
        <label className="text-xs font-medium text-gray-600">
          Tagline
          <span className="ml-1 text-gray-400 font-normal">({settings.tagline.length}/20)</span>
        </label>
        <input
          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
          placeholder="Turbo Assist On-Lot"
          value={settings.tagline}
          maxLength={20}
          onChange={(e) => setSettings((s) => ({ ...s, tagline: e.target.value }))}
          disabled={loading}
        />
        <p className="text-xs text-gray-400">Shown after brand name in the header. Leave blank to hide.</p>
      </div>

      <div className="space-y-1 sm:col-span-2">
        <label className="text-xs font-medium text-gray-600">Logo URL</label>
        <div className="flex items-center gap-2">
          <input
            className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 font-mono"
            placeholder="https://..."
            value={settings.logo_url}
            onChange={(e) => setSettings((s) => ({ ...s, logo_url: e.target.value }))}
            disabled={loading || syncing}
          />
          <button
            type="button"
            onClick={() => void onSyncFromGhl()}
            disabled={loading || syncing}
            className="shrink-0 px-3 py-2 rounded-lg border border-gray-300 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
            title="Pull logo from your GHL location profile"
          >
            {syncing ? "Syncing…" : "Sync from GHL"}
          </button>
        </div>
        <p className="text-xs text-gray-400">Public image URL, or click Sync from GHL to import your location logo.</p>
      </div>

      <div className="space-y-1">
        <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Primary color</label>
        <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-2 border border-gray-200 dark:border-gray-700">
          <input
            type="color"
            className="h-9 w-12 rounded border border-gray-200 dark:border-gray-600 cursor-pointer p-0.5 bg-white dark:bg-gray-700"
            value={settings.color_primary || "#4f46e5"}
            onChange={(e) => setSettings((s) => ({ ...s, color_primary: e.target.value }))}
            disabled={loading}
          />
          <input
            type="text"
            className="flex-1 text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 font-mono bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            placeholder="#4f46e5"
            value={settings.color_primary}
            onChange={(e) => setSettings((s) => ({ ...s, color_primary: e.target.value }))}
            pattern="^#[0-9a-fA-F]{6}$"
            disabled={loading}
          />
          {settings.color_primary && (
            <button
              type="button"
              className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={() => setSettings((s) => ({ ...s, color_primary: "" }))}
              title="Clear (revert to default)"
            >
              ✕
            </button>
          )}
        </div>
        <p className="text-xs text-gray-400">Nav bar, primary buttons. Leave blank for default indigo.</p>
      </div>

      <div className="space-y-1">
        <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Secondary color</label>
        <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-2 border border-gray-200 dark:border-gray-700">
          <input
            type="color"
            className="h-9 w-12 rounded border border-gray-200 dark:border-gray-600 cursor-pointer p-0.5 bg-white dark:bg-gray-700"
            value={settings.color_secondary || "#059669"}
            onChange={(e) => setSettings((s) => ({ ...s, color_secondary: e.target.value }))}
            disabled={loading}
          />
          <input
            type="text"
            className="flex-1 text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 font-mono bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            placeholder="#059669"
            value={settings.color_secondary}
            onChange={(e) => setSettings((s) => ({ ...s, color_secondary: e.target.value }))}
            pattern="^#[0-9a-fA-F]{6}$"
            disabled={loading}
          />
          {settings.color_secondary && (
            <button
              type="button"
              className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={() => setSettings((s) => ({ ...s, color_secondary: "" }))}
              title="Clear (revert to default)"
            >
              ✕
            </button>
          )}
        </div>
        <p className="text-xs text-gray-400">Badges, secondary links. Leave blank for default emerald.</p>
      </div>
    </div>
  );
}
